/**
 * "Let's see your operations together" modal — two-step intake form
 * (identity then operations context) that opens a sales conversation.
 * Engine (`lib/pilot/lead-api.ts`) drives the step toggle and success
 * state. Every `id` preserved verbatim.
 *
 * Includes the `TalkField` helper (used by both step 1 and step 2),
 * scoped to this file since no other modal uses it.
 *
 * Split out of PilotBody.tsx in F18 of the Oatmeal rebuild (see
 * `docs/eng/pilot-oatmeal-rebuild.md`).
 */

import React from 'react';
import {clsx} from 'clsx/lite';
import {Text} from '../../oatmeal/elements/text';
import {Button} from '../../oatmeal/elements/button';
import {Modal} from './_helpers';

export default function PilotTalkModal() {
  return (
    <Modal id="talk-modal">
      <div className="modal-head flex items-center justify-between border-b border-taupe-200 px-6 py-4 dark:border-taupe-800">
        <h3 className="font-display text-2xl/8 text-taupe-950 dark:text-white">
          Let's see your operations together.
        </h3>
        <button
          className="modal-close inline-flex size-9 items-center justify-center rounded-full text-taupe-700 hover:bg-taupe-100 dark:text-taupe-300 dark:hover:bg-taupe-800"
          id="talk-modal-close"
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="modal-body flex flex-col gap-5 overflow-y-auto px-6 py-5" id="talk-modal-body">
        <div className="talk-progress font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
          Step{' '}
          <span className="talk-progress-current text-taupe-950 dark:text-white" id="talk-progress-current">
            1
          </span>{' '}
          of 2 ·{' '}
          <span className="talk-progress-label text-taupe-950 dark:text-white" id="talk-progress-label">
            A quick intro
          </span>
        </div>

        {/* Step 1: identity */}
        <div className="talk-step talk-step-1 flex flex-col gap-4" id="talk-step-1">
          <Text className="talk-modal-intro">
            A 30-minute working session, not a demo. Tell us who you are; on
            the next step we'll ask about the operations you want to fix.
          </Text>
          <div className="talk-grid grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TalkField id="talk-name" label="Name" required type="text" placeholder="Jane Doe" autoComplete="name" />
            <TalkField id="talk-email" label="Work email" required type="email" placeholder="you@company.com" autoComplete="email" />
            <TalkField id="talk-company" label="Company" required type="text" placeholder="Acme Corp" autoComplete="organization" />
            <TalkField id="talk-role" label="Role / title" required type="text" placeholder="COO, Head of Ops, VP Finance…" autoComplete="organization-title" />
            <TalkField id="talk-linkedin" label="LinkedIn URL" type="url" placeholder="https://linkedin.com/in/…" full />
          </div>
          <div className="modal-error min-h-[1.25rem] text-sm/5 text-red-700" id="talk-step1-error" />
          <div className="talk-step-actions flex justify-end">
            <Button className="btn-primary" id="talk-next" size="lg">
              Continue →
            </Button>
          </div>
        </div>

        {/* Step 2: context */}
        <div className="talk-step talk-step-2 hidden flex-col gap-4 [&:not([hidden])]:flex" id="talk-step-2" hidden>
          <Text className="talk-modal-intro">
            Two more questions so we can build the simulation around your
            specific operations. Pre-checked is the process you were just
            looking at.
          </Text>

          <fieldset className="talk-fieldset flex flex-col gap-3 rounded-xl border border-taupe-200 p-4 dark:border-taupe-800">
            <legend className="px-2 font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
              Which operations are slowing you down?
            </legend>
            <div
              id="talk-ops-grid"
              className="talk-checkbox-grid grid grid-cols-1 gap-2 sm:grid-cols-2"
            />
            <input
              type="text"
              id="talk-ops-other"
              className="talk-ops-other rounded-lg border border-taupe-300 bg-white px-3 py-2 text-sm text-taupe-950 placeholder:text-taupe-500 focus:border-taupe-950 focus:outline-none focus:ring-2 focus:ring-taupe-300 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
              placeholder="Other (describe in a few words)"
            />
          </fieldset>

          <label className="modal-label flex flex-col gap-2 text-sm/6 font-medium text-taupe-950 dark:text-white">
            Where does the work fail today? Specific failure points or
            business goals.
            <textarea
              id="talk-goals"
              rows={3}
              placeholder="e.g. Vendor onboarding takes 6 weeks. Signed quotes sit in inboxes. Renewals slip past their dates."
              className="rounded-lg border border-taupe-300 bg-white px-3 py-2 text-sm font-normal text-taupe-950 placeholder:text-taupe-500 focus:border-taupe-950 focus:outline-none focus:ring-2 focus:ring-taupe-300 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
            />
          </label>

          <label className="modal-label flex flex-col gap-2 text-sm/6 font-medium text-taupe-950 dark:text-white">
            What's in your stack today? Rough annual spend on these systems?
            <textarea
              id="talk-stack"
              rows={2}
              placeholder="e.g. Salesforce ($120k/yr), Coupa ($80k/yr), Notion, Slack, plus ~3 full-time ops coordinators."
              className="rounded-lg border border-taupe-300 bg-white px-3 py-2 text-sm font-normal text-taupe-950 placeholder:text-taupe-500 focus:border-taupe-950 focus:outline-none focus:ring-2 focus:ring-taupe-300 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
            />
          </label>

          <div className="modal-error min-h-[1.25rem] text-sm/5 text-red-700" id="talk-modal-error" />
          <div className="talk-step-actions flex items-center justify-between gap-3">
            <Button
              className="btn-secondary border border-taupe-300 bg-white text-taupe-950 hover:bg-taupe-50 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
              id="talk-back"
            >
              ← Back
            </Button>
            <Button className="btn-primary" id="talk-submit" size="lg">
              Request a walkthrough
            </Button>
          </div>
        </div>
      </div>
      <div
        className="modal-body modal-body-success hidden flex-col items-center gap-4 px-6 py-8 text-center [&:not([hidden])]:flex"
        id="talk-modal-success"
        hidden
      >
        <div className="email-success-icon inline-flex size-12 items-center justify-center rounded-full bg-taupe-100 text-2xl text-taupe-950 dark:bg-taupe-900 dark:text-white">
          ✓
        </div>
        <h4 className="email-success-title font-display text-xl/8 text-taupe-950 dark:text-white">
          Got it.
        </h4>
        <p
          className="email-success-body text-sm/6 text-taupe-700 dark:text-taupe-400"
          id="talk-success-text"
        >
          —
        </p>
        <div className="share-link-block flex w-full flex-col gap-2 rounded-xl border border-taupe-200 p-4 text-left dark:border-taupe-800">
          <p className="share-link-label font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
            Want to send this view to a colleague?
          </p>
          <div className="share-link-row flex gap-2">
            <input
              type="text"
              id="share-link-input"
              readOnly
              className="flex-1 rounded-lg border border-taupe-300 bg-white px-3 py-2 text-xs text-taupe-700 focus:outline-none dark:border-taupe-700 dark:bg-taupe-900 dark:text-taupe-300"
            />
            <Button
              className="btn-secondary border border-taupe-300 bg-white text-taupe-950 hover:bg-taupe-50 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
              id="share-link-copy"
            >
              Copy link
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function TalkField({
  id,
  label,
  required,
  type,
  placeholder,
  autoComplete,
  full,
}: {
  id: string;
  label: string;
  required?: boolean;
  type: string;
  placeholder?: string;
  autoComplete?: string;
  full?: boolean;
}) {
  return (
    <label
      className={clsx(
        'modal-label flex flex-col gap-2 text-sm/6 font-medium text-taupe-950 dark:text-white',
        full && 'talk-grid-full sm:col-span-2',
      )}
    >
      {label}{' '}
      <span
        className={clsx(
          'text-xs/5 font-normal',
          required ? 'modal-label-required text-red-700' : 'modal-label-optional text-taupe-500',
        )}
      >
        {required ? 'required' : 'optional'}
      </span>
      <input
        type={type}
        id={id}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="rounded-lg border border-taupe-300 bg-white px-3 py-2 text-sm font-normal text-taupe-950 placeholder:text-taupe-500 focus:border-taupe-950 focus:outline-none focus:ring-2 focus:ring-taupe-300 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
      />
    </label>
  );
}
